/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1901854786 — Інтеграція лояльності з оплатою */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { debug } from '../../../../shared/lib/general';
import { EVENT_NAMES, useSendEventLog, WaitSource } from '../../../event-log';
import { PAYMENT_ROUTES } from '../../../payment/payment.constants';
import { useLoyaltyStore } from '../../hooks';
import { GiftIcon } from '../icons';

import styles from './LoyaltyPaymentButton.module.css';

interface LoyaltyPaymentButtonProps {
    orderId: string | number | null;
    onSuccess?: () => void;
    onError?: () => void;
}

export const LoyaltyPaymentButton: React.FC<LoyaltyPaymentButtonProps> = ({
    orderId,
    onSuccess,
    onError,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const loyaltyUserId = useLoyaltyStore((state) => state.loyalty_user_id);
    const loyaltyId = useLoyaltyStore((state) => state.loyalty_id);
    const isPrizeAvailable = useLoyaltyStore((state) =>
        state.isPrizeAvailable(),
    );
    const isPaying = useLoyaltyStore((state) => state.isPaying);
    const payWithLoyalty = useLoyaltyStore((state) => state.payWithLoyalty);
    const { sendEvent } = useSendEventLog();

    const numberOrderId = !isNaN(Number(orderId)) ? Number(orderId) : 0;

    // Don't show the button if the prize is not available
    if (!isPrizeAvailable) {
        return null;
    }

    const navigateToWaitPage = () => {
        // Timeout to support legacy flow (we can review this later)
        setTimeout(() => {
            navigate(
                generatePath(PAYMENT_ROUTES.wait, {
                    id: String(numberOrderId),
                }),
                {
                    replace: true,
                    state: { waitSource: 'loyalty' satisfies WaitSource },
                },
            );
        }, 100);
    };

    const handlePayment = async () => {
        if (!numberOrderId || !loyaltyId) {
            // Don't proceed if numberOrderId is not a number
            debug('handlePayment: payWithLoyalty DTO is not valid', 'error', {
                loyaltyUserId,
                numberOrderId,
            });
            return;
        }

        sendEvent({ name: EVENT_NAMES.paymentInitiated.Loyalty });

        const success = await payWithLoyalty({
            loyaltyId,
            orderId: numberOrderId,
        });

        if (success) {
            sendEvent({ name: EVENT_NAMES.paymentSuccess.Loyalty });
            onSuccess?.();
            navigateToWaitPage();
        } else {
            sendEvent({ name: EVENT_NAMES.paymentFailed.Loyalty });
            onError?.();
        }
    };

    return (
        <>
            <button
                className={styles.loyaltyButton}
                disabled={isPaying}
                onClick={handlePayment}
            >
                <span className={styles.startIcon}>
                    {isPaying ? (
                        <span className={styles.spinner} />
                    ) : (
                        <GiftIcon />
                    )}
                </span>
                {isPaying
                    ? t('loyalty.paymentButton.processing')
                    : t('loyalty.paymentButton.getFree')}
            </button>

            <div className={styles.divider}>
                <span>{t('loyalty.paymentButton.orPayWithCard')}</span>
            </div>
        </>
    );
};
